import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Trash2, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, FILES_BASE } from "@/lib/api";

export const FORMATS = { "Instagram Post": [1080, 1080], "Story / Reel": [1080, 1920], "Facebook Post": [1200, 630], "WhatsApp Status": [1080, 1920] };
export const HINTS = {
  template: (f) => `Recommended ${FORMATS[f][0]} × ${FORMATS[f][1]} px for ${f}. PNG or JPG, under 8 MB.`,
  logo: () => "PNG with a transparent background works best, at least 500 px wide.",
  building: () => "Landscape photo or render, at least 1200 × 800 px.",
  ambassador: () => "Portrait photo, plain background, at least 800 × 1000 px. Use only photos you have the rights to.",
};
export const DEFAULT_LAYOUT = { logoScale: 1, logoPos: "auto", logoX: 50, logoY: 8, logoMargin: 4, titleScale: 1, msgScale: 1, overlay: 0.35, photoSize: 0.3, photoPos: "bottom", footerAlign: "center", footerScale: 1 };
export const recommendLogo = (s) => (s.building || s.ambassador || s.art === "template" ? "top-center" : "top-left");
export const url = (u) => (!u ? null : u.startsWith("/api/") ? FILES_BASE + u : u);

export function AssetPicker({ kind, projectId, value, onChange, format, canWrite }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = () => api.get("/assets", { params: { kind, project_id: projectId || undefined } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [kind, projectId]); // eslint-disable-line
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind); fd.append("project_id", projectId || "");
    try { const { data } = await api.post("/assets", fd); await load(); onChange(data.file_url); toast.success("Uploaded and saved to the project library"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const remove = async (a) => { await api.delete(`/assets/${a.id}`); if (value === a.file_url) onChange(null); load(); };
  return (
    <div className="space-y-2" data-testid={`asset-picker-${kind}`}>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {canWrite && (
          <button onClick={() => input.current?.click()} className="shrink-0 h-16 w-16 rounded-lg border-2 border-dashed border-slate-700 grid place-items-center text-slate-400 hover:border-emerald-500" data-testid={`asset-upload-${kind}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          </button>
        )}
        {items.map((a) => (
          <div key={a.id} className="relative shrink-0 group">
            <button onClick={() => onChange(value === a.file_url ? null : a.file_url)} data-testid={`asset-${kind}-${a.id}`}
              className={`h-16 w-16 rounded-lg overflow-hidden border-2 ${value === a.file_url ? "border-emerald-500" : "border-transparent"}`}>
              <img src={url(a.file_url)} alt={a.name} className="h-full w-full object-cover" />
              {value === a.file_url && <Check className="absolute top-1 left-1 h-4 w-4 text-white bg-emerald-500 rounded-full p-0.5" />}
            </button>
            {canWrite && <button onClick={() => remove(a)} className="absolute -top-1 -right-1 hidden group-hover:grid h-5 w-5 place-items-center rounded-full bg-rose-500 text-white" title="Delete"><Trash2 className="h-3 w-3" /></button>}
          </div>
        ))}
      </div>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid={`asset-file-${kind}`} />
      <p className="text-[11px] text-slate-500">{HINTS[kind](format)}</p>
    </div>
  );
}

export function ScenePicker({ festival, projectId, value, onChange, canWrite, onCredits }) {
  const [scenes, setScenes] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/greetings/scenes", { params: { festival } }).then((r) => setScenes(r.data.scenes)); }, [festival]);
  const ai = async () => {
    setBusy(true);
    try { const { data } = await api.post("/greetings/scenes/ai", { festival, project_id: projectId || null }); setScenes((s) => [...data.scenes, ...s]); onCredits?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="scene-picker">
      <div className="flex flex-wrap gap-2">{scenes.map((s) => (
        <button key={s} onClick={() => onChange(s)} className={`px-3 py-1.5 rounded-full border text-xs text-left ${value === s ? "border-emerald-500 bg-emerald-500/10 text-emerald-500" : "border-slate-800 text-slate-300 hover:border-slate-600"}`} data-testid="scene-suggestion">{s}</button>))}</div>
      <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Or describe your own scene, e.g. girl doing yoga at sunrise on a terrace garden"
        className="w-full rounded-md border border-slate-800 bg-transparent px-3 py-2 text-sm" data-testid="scene-input" />
      {canWrite && <Button size="sm" variant="secondary" onClick={ai} disabled={busy} data-testid="scene-ai-suggest">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}Suggest with AI (small text credit)</Button>}
    </div>
  );
}

const loadImg = (src) => new Promise((res) => { if (!src) return res(null); const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => res(i); i.onerror = () => res(null); i.src = src; });

function wrap(ctx, text, maxW) {
  const out = [];
  (text || "").split("\n").forEach((para) => {
    let line = "";
    para.split(" ").forEach((w) => { const t = line ? `${line} ${w}` : w; if (ctx.measureText(t).width > maxW && line) { out.push(line); line = w; } else line = t; });
    out.push(line);
  });
  return out;
}

function ornaments(ctx, W, H, sec, style) {
  ctx.strokeStyle = sec; ctx.fillStyle = sec; ctx.lineWidth = Math.max(4, W * 0.006);
  const m = W * 0.04;
  ctx.strokeRect(m, m, W - 2 * m, H - 2 * m);
  [[m, m], [W - m, m], [m, H - m], [W - m, H - m]].forEach(([x, y]) => {
    for (let r = 1; r <= 3; r++) { ctx.globalAlpha = 0.25 * r; ctx.beginPath(); ctx.arc(x, y, W * 0.025 * r, 0, Math.PI * 2); ctx.stroke(); }
  });
  ctx.globalAlpha = style === "Bold" ? 0.18 : 0.1;
  for (let i = 0; i < 14; i++) { ctx.beginPath(); ctx.arc((W / 13) * i, H * 0.97, W * 0.03, Math.PI, 0); ctx.fill(); }
  ctx.globalAlpha = 1;
}

function cover(ctx, img, x, y, w, h) {
  const k = Math.max(w / img.width, h / img.height), sw = w / k, sh = h / k;
  ctx.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, x, y, w, h);
}

async function photos(ctx, s, W, H, u, sec) {
  const [b, a] = await Promise.all([loadImg(url(s.building)), loadImg(url(s.ambassador))]);
  if (!b && !a) return;
  const L = s.layout, size = u * L.photoSize, yb = H - u * (0.1 + 0.045 * (s.footerLines || []).length) - (s.cta ? u * 0.08 : 0) - size;
  const spots = L.photoPos === "corners" ? [[W * 0.07, yb], [W * 0.93 - size, yb]] : b && a ? [[W / 2 - size - u * 0.02, yb], [W / 2 + u * 0.02, yb]] : [[(W - size) / 2, yb]];
  const list = [b && ["b", b], a && ["a", a]].filter(Boolean);
  list.forEach(([k, img], i) => {
    const [x, y] = spots[Math.min(i, spots.length - 1)];
    ctx.save(); ctx.beginPath();
    if (k === "a") ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2); else ctx.roundRect(x, y, size, size, u * 0.025);
    ctx.clip(); cover(ctx, img, x, y, size, size); ctx.restore();
    ctx.strokeStyle = sec; ctx.lineWidth = u * 0.008; ctx.beginPath();
    if (k === "a") ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2); else ctx.roundRect(x, y, size, size, u * 0.025);
    ctx.stroke();
  });
}

export async function drawGreeting(canvas, s) {
  const [W, H] = FORMATS[s.format];
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const L = s.layout;
  const bgSrc = s.art === "upload" ? url(s.template) : s.art === "ai" ? s.bg : null;
  const light = s.style === "Light" && !bgSrc;
  const bg = await loadImg(bgSrc);
  if (bg) { cover(ctx, bg, 0, 0, W, H); ctx.fillStyle = `rgba(0,0,0,${L.overlay})`; ctx.fillRect(0, 0, W, H); }
  else if (light) { ctx.fillStyle = "#FFFDF7"; ctx.fillRect(0, 0, W, H); }
  else if (s.style === "Bold") { ctx.fillStyle = s.primary; ctx.fillRect(0, 0, W, H); ctx.fillStyle = s.secondary; ctx.globalAlpha = 0.85; ctx.beginPath(); ctx.moveTo(0, H * 0.78); ctx.lineTo(W, H * 0.62); ctx.lineTo(W, H); ctx.lineTo(0, H); ctx.fill(); ctx.globalAlpha = 1; }
  else { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, s.primary); g.addColorStop(1, "#0b1220"); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); }
  if (!bg) ornaments(ctx, W, H, s.secondary, s.style);
  const u = Math.min(W, H);
  let y = H * (H > W ? 0.1 : 0.08);
  const logo = await loadImg(url(s.logo));
  if (logo) {
    const pos = L.logoPos === "auto" ? recommendLogo(s) : L.logoPos;
    const lh = u * 0.12 * L.logoScale, lw = Math.min(logo.width * (lh / logo.height), W * 0.5), ph = lw * (logo.height / logo.width);
    const m = (W * L.logoMargin) / 100 + u * 0.03;
    let x = (W - lw) / 2, ly = y;
    if (pos.includes("left")) x = m;
    if (pos.includes("right")) x = W - m - lw;
    if (pos === "top-left" || pos === "top-right") ly = m;
    if (pos.startsWith("bottom")) ly = H - m - ph - u * (0.1 + 0.045 * (s.footerLines || []).length);
    if (pos === "custom") { x = (W * L.logoX) / 100 - lw / 2; ly = (H * L.logoY) / 100; }
    ctx.fillStyle = "#ffffff"; ctx.beginPath(); ctx.roundRect(x - u * 0.03, ly - u * 0.02, lw + u * 0.06, ph + u * 0.04, u * 0.02); ctx.fill();
    ctx.drawImage(logo, x, ly, lw, ph);
    if (pos === "top-center") y += ph + u * 0.1; else if (pos.startsWith("top")) y = Math.max(y + u * 0.04, ly + ph + u * 0.1);
  }
  ctx.textAlign = "center";
  ctx.shadowColor = bg ? "rgba(0,0,0,0.45)" : "transparent"; ctx.shadowBlur = bg ? u * 0.01 : 0;
  ctx.fillStyle = s.secondary; ctx.font = `700 ${u * 0.1 * L.titleScale}px Georgia, serif`;
  wrap(ctx, s.title, W * 0.84).forEach((l) => { ctx.fillText(l, W / 2, y); y += u * 0.11 * L.titleScale; });
  ctx.fillStyle = light ? s.primary : "#ffffff"; ctx.font = `400 ${u * 0.045 * L.msgScale}px "Segoe UI", "Noto Sans", sans-serif`; y += u * 0.02;
  wrap(ctx, s.message, W * 0.8).forEach((l) => { ctx.fillText(l, W / 2, y); y += u * 0.06 * L.msgScale; });
  ctx.shadowBlur = 0;
  await photos(ctx, s, W, H, u, s.secondary);
  const lines = s.footerLines || [];
  const fs = L.footerScale, fx = L.footerAlign === "left" ? W * 0.07 : W / 2;
  let fy = H - u * 0.06 - (lines.length - 1) * u * 0.045 * fs;
  if (s.cta) {
    ctx.textAlign = "center"; ctx.font = `700 ${u * 0.034 * fs}px "Segoe UI", sans-serif`;
    const tw = ctx.measureText(s.cta).width + u * 0.06, cx = L.footerAlign === "left" ? fx + tw / 2 : W / 2, cy = fy - u * 0.1 * fs;
    ctx.fillStyle = s.secondary; ctx.beginPath(); ctx.roundRect(cx - tw / 2, cy - u * 0.035 * fs, tw, u * 0.055 * fs, u * 0.03); ctx.fill();
    ctx.fillStyle = s.primary; ctx.fillText(s.cta, cx, cy + u * 0.004);
  }
  ctx.textAlign = L.footerAlign; ctx.fillStyle = light ? s.primary : "#ffffff";
  lines.forEach((l, i) => { ctx.font = `${i === 0 ? 600 : 400} ${u * (i === 0 ? 0.036 : 0.028) * fs}px "Segoe UI", sans-serif`; ctx.fillText(l, fx, fy); fy += u * 0.045 * fs; });
}
